import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  InputGroup,
  InputLeftElement,
  IconButton,
  Input,
  Spinner,
  useColorModeValue,
  useBreakpointValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Button,
  Badge,
  Avatar,
  Stack,
  Select,
  Tag,
  TagLabel,
  TagLeftIcon,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  Divider,
  Container,
  Image
} from '@chakra-ui/react';
import {
  SearchIcon,
  BellIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  TimeIcon,
  CloseIcon,
  CalendarIcon,
  ViewIcon,
  ExternalLinkIcon
} from '@chakra-ui/icons';
import {
  FiCalendar,
  FiClock,
  FiUsers,
  FiVideo,
  FiRefreshCw,
  FiBookmark,
  FiFilter,
  FiTrendingUp,
  FiPlay,
  FiMapPin,
  FiUser,
  FiBookOpen,
  FiTarget,
  FiStar
} from 'react-icons/fi';
import { motion } from 'framer-motion';

import CalendarComponent from '../shared/Calendar';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';
import { format, isToday, isTomorrow, isPast, isFuture, startOfDay, endOfDay } from 'date-fns';
import { ar } from 'date-fns/locale';
import SidebarInstructor from '../shared/Sidebar3';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const EmptyState = ({ title, description, icon, action }) => {
  return (
    <VStack spacing={6} p={12} textAlign="center">
      <Box color="gray.600">
        {icon}
      </Box>
      <VStack spacing={2}>
        <Heading as="h3" size="md" color="gray.600" fontFamily="Tajawal, sans-serif">
          {title}
        </Heading>
        <Text color="gray.500" fontFamily="Tajawal, sans-serif">
          {description}
        </Text>
      </VStack>
      {action}
    </VStack>
  );
};

const EventCard = ({ event, primaryColor, secondaryColor, textColor }) => {
  const eventDate = new Date(event.start);
  const isEventToday = isToday(eventDate);
  const isEventTomorrow = isTomorrow(eventDate);
  const isEventPast = isPast(eventDate);

  const getEventStatus = () => {
    if (isEventToday) return { text: 'اليوم', color: 'red' };
    if (isEventTomorrow) return { text: 'غداً', color: 'orange' };
    if (isEventPast) return { text: 'انتهى', color: 'gray' };
    return { text: 'قادم', color: 'blue' };
  };

  const status = getEventStatus();

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        bg="white"
        borderRadius="xl"
        boxShadow="md"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
        _hover={{ boxShadow: 'lg' }}
      >
        <CardBody p={6}>
          <Flex justify="space-between" align="start" mb={4}>
            <VStack align="start" spacing={2} flex="1">
              <HStack spacing={2}>
                <Badge
                  colorScheme={status.color}
                  variant="subtle"
                  borderRadius="full"
                  px={3}
                  py={1}
                  fontFamily="Tajawal, sans-serif"
                >
                  {status.text}
                </Badge>
                {event.isLive && (
                  <Badge
                    colorScheme="red"
                    variant="solid"
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontFamily="Tajawal, sans-serif"
                  >
                    🔴 مباشر
                  </Badge>
                )}
              </HStack>

              <Heading
                size="md"
                color={textColor}
                fontFamily="Tajawal, sans-serif"
                fontWeight="600"
                noOfLines={2}
              >
                {event.title}
              </Heading>

              {event.description && (
                <Text
                  color="gray.600"
                  fontSize="sm"
                  fontFamily="Tajawal, sans-serif"
                  noOfLines={2}
                >
                  {event.description}
                </Text>
              )}
            </VStack>

            {event.imageUrl && (
              <Image
                src={event.imageUrl}
                alt={event.title}
                boxSize="60px"
                borderRadius="lg"
                objectFit="cover"
                ml={4}
              />
            )}
          </Flex>

          <VStack spacing={3} align="stretch">
            <HStack spacing={4}>
              <HStack spacing={2}>
                <Icon as={FiCalendar} color="gray.500" boxSize={4} />
                <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                  {format(eventDate, 'dd MMMM yyyy', { locale: ar })}
                </Text>
              </HStack>

              <HStack spacing={2}>
                <Icon as={FiClock} color="gray.500" boxSize={4} />
                <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                  {format(eventDate, 'HH:mm')}
                </Text>
              </HStack>
            </HStack>

            {event.professor && (
              <HStack spacing={2}>
                <Icon as={FiUser} color="gray.500" boxSize={4} />
                <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                  {event.professor}
                </Text>
              </HStack>
            )}

            {event.eventType && (
              <HStack spacing={2}>
                <Icon as={FiBookOpen} color="gray.500" boxSize={4} />
                <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">
                  {getEventTypeLabel(event.eventType)}
                </Text>
              </HStack>
            )}

            <Divider />

            <HStack justify="space-between">
              <HStack spacing={2}>
                {event.isLive && (
                  <Button
                    size="sm"
                    bg={primaryColor}
                    color={secondaryColor}
                    _hover={{ bg: '#d4b016' }}
                    leftIcon={<FiPlay />}
                    borderRadius="lg"
                    fontFamily="Tajawal, sans-serif"
                  >
                    انضم الآن
                  </Button>
                )}

                <IconButton
                  icon={<ViewIcon />}
                  aria-label="عرض التفاصيل"
                  size="sm"
                  variant="ghost"
                  colorScheme="blue"
                />
              </HStack>

              <IconButton
                icon={<FiBookmark />}
                aria-label="حفظ الحدث"
                size="sm"
                variant="ghost"
                colorScheme="gray"
              />
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </motion.div>
  );
};

const getEventTypeLabel = (type) => {
  const types = {
    lecture: 'محاضرة',
    workshop: 'ورشة عمل',
    exam: 'امتحان',
    meeting: 'اجتماع',
    seminar: 'ندوة',
    other: 'أخرى'
  };
  return types[type] || type;
};

const EventsContent = () => {
  const { isOpen } = useSidebar();
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isOpen ? '250px' : '100px' });

  // Color scheme based on home page palette
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'

  const eventTypes = [
    { value: 'all', label: 'جميع الأنواع' },
    { value: 'lecture', label: 'محاضرة' },
    { value: 'workshop', label: 'ورشة عمل' },
    { value: 'exam', label: 'امتحان' },
    { value: 'meeting', label: 'اجتماع' },
    { value: 'seminar', label: 'ندوة' },
    { value: 'other', label: 'أخرى' }
  ];

  const dateFilters = [
    { value: 'all', label: 'جميع التواريخ' },
    { value: 'today', label: 'اليوم' },
    { value: 'tomorrow', label: 'غداً' },
    { value: 'week', label: 'هذا الأسبوع' },
    { value: 'month', label: 'هذا الشهر' },
    { value: 'upcoming', label: 'القادمة' },
    { value: 'past', label: 'السابقة' }
  ];

  useEffect(() => {
    fetchEvents();
    fetchNotifications();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [searchQuery, typeFilter, dateFilter, events]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/events');
      setEvents(response.data || []);
      setError(null);
    } catch (err) {
      console.error('خطأ في جلب الأحداث:', err);
      setError(err.response?.data?.message || 'فشل في جلب الأحداث');
      // Mock data for demonstration
      setEvents([
        {
          id: 1,
          title: 'محاضرة الرياضيات المتقدمة',
          description: 'شرح مفصل للمعادلات التفاضلية',
          start: new Date().toISOString(),
          end: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          professor: 'د. أحمد محمد',
          eventType: 'lecture',
          isLive: true,
          imageUrl: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb'
        },
        {
          id: 2,
          title: 'امتحان الفيزياء النووية',
          description: 'امتحان نهائي للفصل الدراسي',
          start: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
          end: new Date(Date.now() + 26 * 60 * 60 * 1000).toISOString(),
          professor: 'د. فاطمة علي',
          eventType: 'exam',
          isLive: false
        },
        {
          id: 3,
          title: 'ورشة عمل الكيمياء العضوية',
          description: 'تطبيقات عملية في المختبر',
          start: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
          end: new Date(Date.now() + 51 * 60 * 60 * 1000).toISOString(),
          professor: 'د. محمد حسن',
          eventType: 'workshop',
          isLive: false,
          imageUrl: 'https://images.unsplash.com/photo-1532094349884-543bc11b234d'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axiosInstance.get('/notifications');
      setNotifications(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب الإشعارات:', error);
      // Mock notifications
      setNotifications([
        { id: 1, message: 'محاضرة جديدة متاحة' },
        { id: 2, message: 'تذكير بامتحان غداً' }
      ]);
    }
  };

  const filterEvents = () => {
    let results = events;

    // Search filter
    if (searchQuery) {
      results = results.filter(event =>
        (event.title && event.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (event.description && event.description.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (event.professor && event.professor.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Type filter
    if (typeFilter !== 'all') {
      results = results.filter(event => event.eventType === typeFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const today = startOfDay(now);
      const tomorrow = startOfDay(new Date(now.getTime() + 24 * 60 * 60 * 1000));

      results = results.filter(event => {
        const eventDate = new Date(event.start);

        switch (dateFilter) {
          case 'today':
            return isToday(eventDate);
          case 'tomorrow':
            return isTomorrow(eventDate);
          case 'week':
            const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
            return eventDate >= today && eventDate <= weekFromNow;
          case 'month':
            const monthFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
            return eventDate >= today && eventDate <= monthFromNow;
          case 'upcoming':
            return isFuture(eventDate);
          case 'past':
            return isPast(eventDate);
          default:
            return true;
        }
      });
    }

    setFilteredEvents(results);
  };

  const handleRetry = () => {
    setError(null);
    fetchEvents();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setTypeFilter('all');
    setDateFilter('all');
  };

  const getEventStats = () => {
    const totalEvents = events.length;
    const todayEvents = events.filter(event => isToday(new Date(event.start))).length;
    const upcomingEvents = events.filter(event => isFuture(new Date(event.start))).length;
    const liveEvents = events.filter(event => event.isLive).length;

    return { totalEvents, todayEvents, upcomingEvents, liveEvents };
  };

  const stats = getEventStats();

  if (loading) {
    return (
      <Flex justify="center" align="center" height="100vh" dir="rtl">
        <VStack spacing={4}>
          <Spinner
            size="xl"
            color={primaryColor}
            thickness="4px"
          />
          <Text color={textColor} fontFamily="Tajawal, sans-serif">جاري تحميل الأحداث...</Text>
        </VStack>
      </Flex>
    );
  }

  if (error && events.length === 0) {
    return (
      <Flex justify="center" align="center" height="100vh" p={4} dir="rtl">
        <Alert
          status="error"
          borderRadius="xl"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          minHeight="200px"
          bg={cardBg}
          maxW="md"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} fontSize="lg" fontFamily="Tajawal, sans-serif">
            خطأ في تحميل الأحداث
          </AlertTitle>
          <AlertDescription mt={2} fontFamily="Tajawal, sans-serif">
            {error}
          </AlertDescription>
          <Button
            mt={4}
            bg={primaryColor}
            color={secondaryColor}
            _hover={{ bg: '#d4b016' }}
            onClick={handleRetry}
            fontFamily="Tajawal, sans-serif"
          >
            إعادة المحاولة
          </Button>
        </Alert>
      </Flex>
    );
  }

  return (
    <Box dir="rtl" fontFamily="Tajawal, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <SidebarInstructor />

        <Box
          flex="1"
          mr={contentMarginRight}
          p={6}
          bg={bgColor}
          transition="margin-right 0.3s ease"
          overflowY="auto"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading
                      as="h1"
                      size="xl"
                      color={secondaryColor}
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="700"
                    >
                      الأحداث والفعاليات
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      تابع جدولك الأكاديمي والأحداث المهمة
                    </Text>
                  </VStack>

                  <HStack spacing={4} flexDirection={{ base: 'column', md: 'row' }}>
                    <InputGroup maxW="300px">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.600" />
                      </InputLeftElement>
                      <Input
                        placeholder="البحث في الأحداث..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        bg="gray.50"
                        border="2px solid"
                        borderColor="gray.200"
                        _focus={{
                          borderColor: primaryColor,
                          boxShadow: `0 0 0 1px ${primaryColor}`
                        }}
                        borderRadius="lg"
                      />
                    </InputGroup>

                    <Menu>
                      <MenuButton as={IconButton} icon={<BellIcon />} bg="white" border="2px solid" borderColor="gray.200">
                        {notifications.length > 0 && (
                          <Badge
                            position="absolute"
                            top="-1"
                            left="-1"
                            colorScheme="red"
                            borderRadius="full"
                            boxSize="20px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontSize="xs"
                          >
                            {notifications.length}
                          </Badge>
                        )}
                      </MenuButton>
                      <MenuList>
                        {notifications.length > 0 ? (
                          notifications.map((notification) => (
                            <MenuItem key={notification.id} fontFamily="Tajawal, sans-serif">
                              {notification.message}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem fontFamily="Tajawal, sans-serif">لا توجد إشعارات جديدة</MenuItem>
                        )}
                      </MenuList>
                    </Menu>

                    <Avatar
                      size="md"
                      name={user?.username}
                      bg={primaryColor}
                      color={secondaryColor}
                      cursor="pointer"
                      border="2px solid"
                      borderColor="gray.200"
                    />
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Statistics */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            إجمالي الأحداث
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.totalEvents}
                          </StatNumber>
                          <StatHelpText color="blue.500" fontSize="sm">
                            جميع الأحداث
                          </StatHelpText>
                        </Box>
                        <Box
                          bg={`${primaryColor}20`}
                          p={3}
                          borderRadius="lg"
                        >
                          <Icon as={FiCalendar} color={primaryColor} boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            أحداث اليوم
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.todayEvents}
                          </StatNumber>
                          <StatHelpText color="green.500" fontSize="sm">
                            <StatArrow type="increase" />
                            مجدولة اليوم
                          </StatHelpText>
                        </Box>
                        <Box
                          bg="green.100"
                          p={3}
                          borderRadius="lg"
                        >
                          <Icon as={FiClock} color="green.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            الأحداث القادمة
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.upcomingEvents}
                          </StatNumber>
                          <StatHelpText color="blue.500" fontSize="sm">
                            في المستقبل
                          </StatHelpText>
                        </Box>
                        <Box
                          bg="blue.100"
                          p={3}
                          borderRadius="lg"
                        >
                          <Icon as={FiTrendingUp} color="blue.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>

              <motion.div variants={fadeIn}>
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardBody p={6}>
                    <Stat>
                      <Flex align="center" justify="space-between">
                        <Box>
                          <StatLabel color="gray.600" fontSize="sm" fontWeight="500">
                            البث المباشر
                          </StatLabel>
                          <StatNumber color={secondaryColor} fontSize="2xl" fontWeight="bold">
                            {stats.liveEvents}
                          </StatNumber>
                          <StatHelpText color="red.500" fontSize="sm">
                            أحداث مباشرة
                          </StatHelpText>
                        </Box>
                        <Box
                          bg="red.100"
                          p={3}
                          borderRadius="lg"
                        >
                          <Icon as={FiVideo} color="red.500" boxSize={6} />
                        </Box>
                      </Flex>
                    </Stat>
                  </CardBody>
                </Card>
              </motion.div>
            </SimpleGrid>
          </motion.div>

          {/* Filters */}
          <motion.div variants={fadeIn}>
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex gap={4} flexDirection={{ base: 'column', md: 'row' }} align="center">
                  <Select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    maxW="200px"
                    bg="gray.50"
                    border="2px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                  >
                    {eventTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    maxW="200px"
                    bg="gray.50"
                    border="2px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                  >
                    {dateFilters.map((filter) => (
                      <option key={filter.value} value={filter.value}>
                        {filter.label}
                      </option>
                    ))}
                  </Select>

                  <HStack spacing={2}>
                    <Button
                      leftIcon={<FiFilter />}
                      variant="outline"
                      borderRadius="lg"
                      onClick={clearFilters}
                      fontFamily="Tajawal, sans-serif"
                    >
                      إعادة تعيين
                    </Button>

                    <Button
                      leftIcon={<FiRefreshCw />}
                      bg={primaryColor}
                      color={secondaryColor}
                      _hover={{ bg: '#d4b016' }}
                      borderRadius="lg"
                      onClick={fetchEvents}
                      isLoading={loading}
                      fontFamily="Tajawal, sans-serif"
                    >
                      تحديث
                    </Button>
                  </HStack>

                  <Text fontSize="sm" color="gray.500" fontFamily="Tajawal, sans-serif">
                    عرض {filteredEvents.length} من {events.length} حدث
                  </Text>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Events Content */}
          <motion.div variants={fadeIn}>
            {filteredEvents.length > 0 ? (
              viewMode === 'calendar' ? (
                <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                  <CardHeader>
                    <Flex justify="space-between" align="center">
                      <Heading size="md" color={secondaryColor} fontFamily="Tajawal, sans-serif">
                        التقويم التفاعلي
                      </Heading>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewMode(viewMode === 'calendar' ? 'list' : 'calendar')}
                        fontFamily="Tajawal, sans-serif"
                      >
                        {viewMode === 'calendar' ? 'عرض القائمة' : 'عرض التقويم'}
                      </Button>
                    </Flex>
                  </CardHeader>
                  <CardBody>
                    <CalendarComponent
                      events={filteredEvents}
                      isLoading={loading}
                      error={error}
                    />
                  </CardBody>
                </Card>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {filteredEvents.map((event) => (
                    <EventCard
                      key={event.id}
                      event={event}
                      primaryColor={primaryColor}
                      secondaryColor={secondaryColor}
                      textColor={textColor}
                    />
                  ))}
                </SimpleGrid>
              )
            ) : (
              <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
                <CardBody>
                  <EmptyState
                    title="لم يتم العثور على أحداث"
                    description="حاول تعديل بحثك أو فلاترك للعثور على أحداث"
                    icon={<Icon as={FiCalendar} boxSize="3rem" color="gray.600" />}
                    action={
                      <Button
                        bg={primaryColor}
                        color={secondaryColor}
                        _hover={{ bg: '#d4b016' }}
                        onClick={clearFilters}
                        fontFamily="Tajawal, sans-serif"
                      >
                        مسح الفلاتر
                      </Button>
                    }
                  />
                </CardBody>
              </Card>
            )}
          </motion.div>
        </Box>
      </Flex>
    </Box>
  );
};

const InstructorEvents = () => {
  return (
    <EventsContent />
  );
};

export default InstructorEvents;


